import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { ExtendedHeroSpot, IImage } from '@ncg/data';
import { CinemaAspect, MobileAspectRatio } from '../../utils/helpers/aspect-ratio';
import { breakpointQueries, breakpoints } from '../../utils/helpers/breakpoints';
import { ImageUrl } from '../../utils/helpers/image-helper';
import { SpotBaseDirective } from '../spot-base.class';

@Component({
    selector: 'ncg-extended-hero-spot',
    template: `
        <div class="extended-hero-spot">
            <div
                class="extended-hero-spot__container"
                [ngClass]="{
                    'extended-hero-spot__container--full-height': data.fullHeight,
                    'extended-hero-spot__container--full-height--is-first': data.fullHeight && isFirstSpot,
                    'extended-hero-spot__container--full-height--is-first--breadcrumb-overlaid':
                        data.fullHeight && isFirstSpot && data.breadcrumbOverlaid,
                }">
                <ncg-link
                    *ngIf="data.links && data.links.length === 1"
                    [link]="data.links[0]"
                    styleClass="extended-hero-spot__link"
                    [tabindex]="-1"
                    (mouseenter)="onFullSizeLinkHover(true)"
                    (mouseleave)="onFullSizeLinkHover(false)"></ncg-link>
                <ncg-video
                    class="extended-hero-spot__media-container"
                    [ngClass]="{ 'extended-hero-spot__media-container--media-filter': data.mediaFilter }"
                    *ngIf="data.video && data.video?.url; else imageTmpl"
                    [video]="data.video"
                    [poster]="data.image"
                    [preload]="isFirstSpot ? 'auto' : 'metadata'"></ncg-video>
                <ng-template #imageTmpl>
                    <picture
                        *ngIf="data.image as image"
                        class="extended-hero-spot__media-container"
                        [ngClass]="{
                            'extended-hero-spot__media-container--media-filter': data.mediaFilter,
                        }">
                        <source [srcset]="mobileSrcset" [media]="breakpointQueries.mobile" />
                        <source [srcset]="mobileSrcset" [media]="breakpointQueries.mobileLarge" />
                        <source [srcset]="mobileSrcset" [media]="breakpointQueries.tablet" />
                        <source [srcset]="desktopSrcset" [media]="breakpointQueries.desktop" />

                        <ng-container *ngIf="isFirstSpot; else defaultImageTmpl">
                            <img
                                [src]="image | imageUrl: { width: 1440, mode: 'crop' }"
                                [alt]="image.altText || data.title || ''"
                                sizes="97vw"
                                fetchpriority="high" />
                        </ng-container>
                        <ng-template #defaultImageTmpl>
                            <img
                                [src]="image | imageUrl: { width: 1440, mode: 'crop' }"
                                [alt]="image.altText || data.title || ''"
                                sizes="97vw"
                                loading="lazy"
                                ncgImageLoad />
                        </ng-template>
                    </picture>
                </ng-template>
                <div
                    class="extended-hero-spot__content"
                    [class.extended-hero-spot__content--alternate]="data.alternateLayout"
                    ncgLoadIn
                    *ngIf="data.title || data.subtitle || data.price || data.links || data.titleImage">
                    <figure class="extended-hero-spot__title-image" *ngIf="data.titleImage">
                        <img
                            [src]="data.titleImage | imageUrl: { width: 500, mode: 'max' }"
                            [alt]="data.title + (data.subtitle ? ' - ' + data.subtitle : '')"
                            loading="lazy"
                            ncgImageLoad />
                    </figure>
                    <ng-container *ngIf="data.useAsHeader; else noHeader">
                        <h1
                            [class.extended-hero-spot__visually-hidden]="data.titleImage"
                            *ngIf="data.title"
                            class="extended-hero-spot__title"
                            [innerHTML]="data.title | usedCarsTags | async | safe: 'html'"></h1>
                        <div
                            [class.extended-hero-spot__visually-hidden]="data.titleImage"
                            *ngIf="data.subtitle"
                            class="extended-hero-spot__subtitle"
                            ncgInterceptLinks
                            [innerHTML]="data.subtitle | usedCarsTags | async | safe: 'html'"></div>
                    </ng-container>
                    <ng-template #noHeader>
                        <h2
                            [class.extended-hero-spot__visually-hidden]="data.titleImage"
                            *ngIf="data.title"
                            class="extended-hero-spot__title"
                            [innerHTML]="data.title | usedCarsTags | async | safe: 'html'"></h2>
                        <div
                            [class.extended-hero-spot__visually-hidden]="data.titleImage"
                            *ngIf="data.subtitle"
                            class="extended-hero-spot__subtitle"
                            ncgInterceptLinks
                            [innerHTML]="data.subtitle | usedCarsTags | async | safe: 'html'"></div>
                    </ng-template>
                    <p *ngIf="data.price" class="extended-hero-spot__price">
                        {{ 'models.pricefrom' | translate: { price: data.price } }}
                    </p>
                    <div class="extended-hero-spot__cta" *ngIf="data.links">
                        <ncg-button
                            *ngFor="let link of data.links; let first = first"
                            [link]="link"
                            [isHoverActive]="isHovered"
                            [buttonClass]="first ? 'is-extended-hero' : 'is-extended-hero-secondary'"></ncg-button>
                    </div>
                </div>
                <div
                    *ngIf="showLegal"
                    class="extended-hero-spot__legal-on-hero"
                    [ngClass]="{
                        'is-hidden-touch': data.showLegalTextUnderMobile,
                        'is-hidden-desktop': data.showLegalTextUnderDesktop,
                    }">
                    <div class="extended-hero-spot__overlay"></div>
                    <ng-container *ngTemplateOutlet="legal"></ng-container>
                </div>
            </div>
            <div
                class="extended-hero-spot__legal-sub-hero"
                [ngClass]="{
                    'is-hidden-touch': !data.showLegalTextUnderMobile,
                    'is-hidden-desktop': !data.showLegalTextUnderDesktop,
                }"
                *ngIf="showLegal && (data.showLegalTextUnderMobile || data.showLegalTextUnderMobile)">
                <ng-container *ngTemplateOutlet="legal"></ng-container>
            </div>

            <ng-template #legal>
                <div class="extended-hero-spot__legal">
                    <ncg-legal
                        [energyLabels]="data.legal?.energyLabels"
                        [legalText]="'<b>' + data.legal?.modelName + ':</b> ' + ' ' + data.legal?.legalText"></ncg-legal>
                    <ng-container *ngFor="let item of data.additionalLegal" class="extra-legal">
                        <ncg-legal
                            [energyLabels]="item.energyLabels"
                            [legalText]="'<b>' + item.modelName + ':</b> ' + ' ' + item.legalText"></ncg-legal>
                    </ng-container>
                    <ncg-legal [legalText]="data.legal?.extraHeroText"></ncg-legal>
                </div>
            </ng-template>
        </div>
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class ExtendedHeroSpotComponent extends SpotBaseDirective implements OnInit {
    public static ref = 'extendedhero';

    public desktopAspectRatio: number = CinemaAspect;
    public mobileAspectRatio: number = MobileAspectRatio;
    public mobileSrcset: string;
    public desktopSrcset: string;
    public srcset: string;
    public breakpointQueries = breakpointQueries;
    public showLegal: boolean = false;
    public isHovered: boolean = false;

    @Input()
    public data: ExtendedHeroSpot;

    constructor(private readonly cd: ChangeDetectorRef) {
        super();
    }

    public ngOnInit() {
        this.showLegal = Boolean(
            this.data.legal &&
                (this.data.legal.energyLabels || this.data.legal.legalText || this.data.additionalLegal?.length || this.data.legal.extraHeroText)
        );
        this.cd.markForCheck();
        if (this.data.image) {
            this.generateSrcset(this.data.image);
        }
    }

    public onFullSizeLinkHover(isHover: boolean) {
        this.isHovered = isHover;
    }

    private generateSrcset(image: IImage | undefined): void {
        if (!image || !image.url) {
            return;
        }

        const mobileSrcsetArray = new Set();
        const desktopSrcsetArray = new Set();

        for (const width of Object.values(breakpoints)) {
            const imageOptions: Parameters<typeof ImageUrl>[1] = {
                mode: 'crop',
            };

            // if original image width is known, only try to add images that are smaller than the image width to avoid upscaling.
            const maxWidth = image.width || 1440;
            const strWidth = width <= maxWidth ? width : maxWidth;
            const strRetinaWidth = width * 2 <= maxWidth ? width * 2 : maxWidth;

            if (width < breakpoints.tablet) {
                imageOptions.heightratio = this.mobileAspectRatio;
                const str = `${ImageUrl(image, { ...imageOptions, width: strWidth })} ${width}w`;
                const retinaStr = `${ImageUrl(image, { ...imageOptions, width: strRetinaWidth })} ${width * 2}w`;
                mobileSrcsetArray.add(str);
                mobileSrcsetArray.add(retinaStr);
            } else {
                imageOptions.heightratio = this.desktopAspectRatio;
                const str = `${ImageUrl(image, { ...imageOptions, width: strWidth })} ${width}w`;
                const retinaStr = `${ImageUrl(image, { ...imageOptions, width: strRetinaWidth })} ${width * 2}w`;
                desktopSrcsetArray.add(str);
                desktopSrcsetArray.add(retinaStr);
            }
        }

        this.mobileSrcset = Array.from(mobileSrcsetArray).join(', ');
        this.desktopSrcset = Array.from(desktopSrcsetArray).join(', ');
        this.cd.markForCheck();
    }
}
